import {
  Dispatch,
  Reducer,
  ReducerAction,
  ReducerState,
  useCallback,
  useEffect,
  useReducer,
} from 'react'

import { useHasChanged } from './usePrevious'

export interface IFetchResult<T> {
  isLoading: boolean
  data: T
}

interface ResolvedAction<T> {
  type: 'resolved'
  data: T
}

interface FailedAction {
  type: 'failed'
}

interface ResetAction {
  type: 'reset'
}

type FetchAction<T> = ResolvedAction<T> | FailedAction | ResetAction
type FetchReducer<T> = Reducer<IFetchResult<T>, FetchAction<T>>

function createFetchOneReducer<T>(): FetchReducer<T | undefined> {
  // tslint:disable-next-line:no-shadowed-variable
  return function fetchReducer<T>(
    _state: IFetchResult<T | undefined>,
    action: FetchAction<T | undefined>
  ) {
    switch (action.type) {
      case 'resolved':
        return {
          isLoading: false,
          data: action.data,
        }

      case 'failed':
      case 'reset':
        return {
          isLoading: false,
          data: undefined,
        }
    }
  }
}

function useFetchReducer<T>(
  fetch: () => Promise<{ data: T }>,
  [state, dispatch]: [ReducerState<FetchReducer<T>>, Dispatch<ReducerAction<FetchReducer<T>>>],
  initialState: ReducerState<FetchReducer<T>>,
  skipLoadingFlag?: boolean
): IFetchResult<T> {
  useEffect(() => {
    let isCancelled = false

    async function fetchData() {
      try {
        const { data } = await fetch()
        if (!isCancelled) {
          dispatch({
            type: 'resolved',
            data,
          })
        }
      } catch (err) {
        console.error(err)
        if (!isCancelled) {
          dispatch({
            type: 'failed',
          })
        }
      }
    }

    fetchData()

    return () => {
      isCancelled = true
    }
  }, [fetch, dispatch, skipLoadingFlag])

  // synchronously force reset state to avoid initial rerender with stale state
  if (useHasChanged(fetch) && !skipLoadingFlag) {
    return initialState
  }

  return state
}

export function useFetchOne<T>(
  fetch: () => Promise<T | undefined>,
  skipLoadingFlag?: boolean
): IFetchResult<T | undefined> {
  const initialState = {
    isLoading: true,
    data: undefined,
  }

  const reducer = useReducer(createFetchOneReducer<T | undefined>(), initialState)
  const fetchCallback = useCallback(
    () => fetch().then((data) => ({ data, meta: undefined })),
    [fetch]
  )
  return useFetchReducer(fetchCallback, reducer, initialState, skipLoadingFlag)
}
